import React, { useRef } from "react";
import { motion, useInView } from "framer-motion";

const Facilities = () => {
  const containerRef = useRef(null);
  const isInView = useInView(containerRef, { once: true, threshold: 0.1 });

  const leaders = [
    {
      id: 1,
      name: "Mr. Christob Rajus",
      position: "Chairman",
      image:
        "https://res.cloudinary.com/ddvpczzng/image/upload/f_auto,q_auto:best,w_500,h_500,c_fill,g_face/v1757446084/1.Chairman_vwat7q.jpg",
    },
    {
      id: 2,
      name: "Mrs. C. Shanthi",
      position: "Senior Principal",
      image:
        "https://res.cloudinary.com/ddvpczzng/image/upload/f_auto,q_auto:best,w_500,h_500,c_fill,g_face/v1757446078/2.Senior_Principal_csrwel.jpg",
    },
    {
      id: 3,
      name: "Mr. C. Joshua Glenn Mathew",
      position: "Correspondent",
      image:
        "https://res.cloudinary.com/ddvpczzng/image/upload/f_auto,q_auto:best,w_500,h_500,c_fill,g_face/v1757446074/3.Correspondent_whla8f.jpg",
    },
  ];

  const cardVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: i * 0.15,
        duration: 0.4,
        ease: "easeOut",
      },
    }),
  };

  return (
    <section
      className="relative w-screen left-1/2 right-1/2 mx-[-50vw] overflow-hidden 
        -mt-12 md:-mt-16 lg:-mt-20 
        mb-[-48px] md:mb-[-64px] lg:mb-[-80px] 
        pt-16 md:pt-20 lg:pt-24 
        pb-16 md:pb-20 lg:pb-24 
        bg-gradient-to-br from-gray-300 via-white to-gray-400"
      ref={containerRef}
      id="pillars"
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Section Header */}
        <motion.div
          className="text-center mb-10 sm:mb-12 md:mb-16"
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-3 mt-4">
            Our <span className="text-[#c5935a]">Pillars</span>
          </h1>
          <p className="text-base sm:text-lg md:text-xl text-gray-700 font-serif max-w-2xl mx-auto">
            Visionary leaders guiding our institution
          </p>
        </motion.div>

        {/* Cards Grid */}
        <div className="grid grid-cols-1 xs:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 md:gap-5 justify-items-center">
          {leaders.map((leader, i) => (
            <motion.div
              key={leader.id}
              className="relative rounded-lg overflow-hidden shadow-md cursor-pointer w-full max-w-[250px] hover:shadow-lg transition-all duration-300 group"
              variants={cardVariants}
              initial="hidden"
              animate={isInView ? "visible" : "hidden"}
              custom={i}
              whileHover={{ scale: 1.03 }}
            >
              {/* Image */}
              <div className="aspect-[4/5] w-full overflow-hidden">
                <img
                  src={leader.image}
                  alt={`${leader.name}, ${leader.position}`}
                  loading="lazy"
                  width="500"
                  height="500"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  decoding="async"
                />
              </div>

              {/* Overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent flex flex-col justify-end p-2 sm:p-3">
                <div className="text-center transform translate-y-1 group-hover:translate-y-0 transition-transform duration-300">
                  <h3 className="text-white text-base sm:text-lg font-bold drop-shadow-lg leading-tight">
                    {leader.position}
                  </h3>
                  <p className="text-gray-200 text-xs sm:text-sm mt-0.5 drop-shadow-md leading-tight">
                    {leader.name}
                  </p>
                </div>
              </div>
            </motion.div>
          ))}

          {/* Commitment Card */}
          <motion.div
            className="bg-white rounded-lg shadow-md hover:shadow-lg w-full max-w-[250px] flex flex-col transition-all duration-300 aspect-[4/5] p-4 sm:p-5"
            variants={cardVariants}
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
            custom={leaders.length}
            whileHover={{ scale: 1.03 }}
          >
            <h3 className="text-base sm:text-lg md:text-xl mb-2 text-gray-900 font-serif font-bold text-center leading-snug">
              Our Commitment
            </h3>
            <p className="text-gray-700 text-sm sm:text-base mb-2 text-justify leading-snug font-serif">
              We are united in our purpose to nurture an educational space where
              excellence is a habit and growth is a constant.
            </p>
            <div className="bg-gray-50 p-3 rounded-md border-l-4 border-gray-900 hover:bg-gray-100 transition-colors duration-300">
              <p className="text-gray-800 text-sm sm:text-base text-justify leading-snug font-serif">
                Every decision we make is rooted in a deep sense of
                responsibility towards our students, their well-being, and their
                future.
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Facilities;
